import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Upload, Download } from 'lucide-react';
import { downloadExport } from '../utils/exportUtils';
import { calcInternalDue, internalDueColor, internalDueBadge, DEFAULT_BUFFERS } from '../utils/dueDate';
import api from '../utils/api';
import usePersistedFilter from '../utils/usePersistedFilter';
import { fmtDate, fmtCurrency, FY_OPTIONS, MONTHS_FY, statusColor, canDelete, canImport } from '../utils/helpers';
import { PageHeader, DataTable, SearchBar, StatusBadge, Modal, FormField } from '../components/Table';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import ConfirmModal from '../components/ConfirmModal';
import ModuleImport from '../components/ModuleImport';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import BulkActionBar from '../components/BulkActionBar';
import BulkEmailModal from '../components/BulkEmailModal';
import WhatsAppModal from '../components/WhatsAppModal';

export default function ESIPFPage() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [fyFilter, setFyFilter] = usePersistedFilter('pms_filter_fy_esipf');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const canDel = canDelete(user);
  const [buffers, setBuffers] = useState(DEFAULT_BUFFERS);
  useEffect(() => { api.get('/settings/buffer').then(r => setBuffers(b => ({ ...b, ...r.data }))).catch(() => {}); }, []);

  // Bulk actions (Feature 3)
  const [selected, setSelected] = useState(new Set());
  const [waTargets, setWaTargets] = useState(null);
  const [emailTargets, setEmailTargets] = useState(null);
  const toggleSel = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllSel = rows => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  const selRows = () => filtered.filter(r => selected.has(r.id));
  const bulkUpdate = async updates => { try { await api.post('/esipf/monthly/bulk-update', { ids: [...selected], updates }); toast.success('Updated'); setSelected(new Set()); load(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkDelete = async () => { try { await api.post('/esipf/monthly/bulk-delete', { ids: [...selected] }); toast.success('Deleted'); setSelected(new Set()); load(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkTargets = () => { const seen = new Set(); return selRows().filter(r => { if (seen.has(r.client_id)) return false; seen.add(r.client_id); return true; }).map(r => ({ id: r.client_id, client_id: r.client_id, name: r.client_name, whatsapp: r.whatsapp, email: r.client_email })); };
  const canImp = canImport(user);

  const load = () => {
    const params = {}; if (fyFilter) params.fy = fyFilter;
    if (showArchived) params.show_archived = '1';
    api.get('/esipf/monthly', { params }).then(r => setData(r.data));
  };

  useEffect(() => { load(); }, [fyFilter, showArchived]);

  // Alt+I — open the Add form ready to type
  useEffect(() => {
    const h = () => { setEditing(null); setForm({ fy: '2025-26', pf_status: 'Pending', esi_status: 'Pending' }); setShowModal(true); };
    window.addEventListener('pms:new-record', h);
    return () => window.removeEventListener('pms:new-record', h);
  }, []);

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/esipf/monthly/${editing.id}`, form);
      else await api.post('/esipf/monthly', form);
      toast.success('Saved'); setShowModal(false); setEditing(null); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.error || e.message || 'Failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/esipf/monthly/${deleteTarget.id}`); toast.success('Record deleted'); setDeleteTarget(null); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const filtered = data.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return d.client_name?.toLowerCase().includes(s) || d.client_code?.toLowerCase().includes(s);
  });

  // Summary stats
  const pfOverdue = filtered.filter(d => d.pf_status === 'Pending').length;
  const esiOverdue = filtered.filter(d => d.esi_status === 'Pending').length;

  const columns = [
    { key: 'client_name', label: 'Client', render: (v, row) => (
      <div><div className="font-medium text-gray-800 text-sm">{v}</div>
        {row.client_code && <div className="text-xs text-gray-400">{row.client_code}</div>}
      </div>
    )},
    { key: 'month', label: 'Month' },
    { key: 'pf_amount', label: 'PF Amount', render: v => fmtCurrency(v) },
    { key: 'pf_date_paid', label: 'PF Paid On', render: v => fmtDate(v) },
    { key: 'pf_trrn', label: 'TRRN' },
    { key: 'pf_status', label: 'PF Status', render: (v, row) => (
      <select value={v} onChange={async e => { await api.put(`/esipf/monthly/${row.id}`, { ...row, pf_status: e.target.value }); load(); }}
        className={`text-xs rounded-full px-2 py-0.5 border-0 cursor-pointer ${statusColor(v)}`}>
        {['Pending', 'Paid', 'Paid Late'].map(s => <option key={s}>{s}</option>)}
      </select>
    )},
    { key: 'esi_amount', label: 'ESI Amount', render: v => fmtCurrency(v) },
    { key: 'esi_date_paid', label: 'ESI Paid On', render: v => fmtDate(v) },
    { key: 'esi_status', label: 'ESI Status', render: (v, row) => (
      <select value={v} onChange={async e => { await api.put(`/esipf/monthly/${row.id}`, { ...row, esi_status: e.target.value }); load(); }}
        className={`text-xs rounded-full px-2 py-0.5 border-0 cursor-pointer ${statusColor(v)}`}>
        {['Pending', 'Paid', 'Paid Late'].map(s => <option key={s}>{s}</option>)}
      </select>
    )},
    { key: 'internal_due_date', label: 'Internal Due', render: (v, row) => v
      ? <span className={internalDueColor(v, row.pf_status === 'Paid' && row.esi_status === 'Paid' ? 'Paid' : 'Pending')}>{internalDueBadge(v, row.pf_status === 'Paid' ? 'Paid' : 'Pending')} {fmtDate(v)}</span>
      : <span className="text-gray-300">—</span>
    },
    { key: 'id', label: '', sortable: false, render: (v, row) => (
      <div className="flex gap-2 items-center">
        <button onClick={() => { setEditing(row); setForm(row); setShowModal(true); }} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>
        <button title={row.is_archived ? 'Unarchive' : 'Archive'} onClick={async () => { await api.patch(`/esipf/monthly/${row.id}/${row.is_archived ? 'unarchive' : 'archive'}`); toast.success(row.is_archived ? 'Unarchived' : 'Archived'); load(); }} className={`text-xs ${row.is_archived ? 'text-amber-600' : 'text-gray-300 hover:text-amber-600'}`}>{row.is_archived ? '↩' : '📁'}</button>
        {canDel && <button onClick={() => setDeleteTarget(row)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>}
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader title="ESI & PF" subtitle="Monthly compliance tracker"
        actions={
          <div className="flex gap-2">
            <button onClick={() => downloadExport('/export/esipf', { fy: fyFilter }, `KUA_ESIPF_${new Date().toISOString().split('T')[0]}.xlsx`)} className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>
            {canImp && <button onClick={() => setShowImport(true)} className="btn-secondary flex items-center gap-2 text-sm"><Upload size={15} /> Import</button>}
            <button onClick={() => { setEditing(null); setForm({ fy: '2025-26', pf_status: 'Pending', esi_status: 'Pending' }); setShowModal(true); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Record</button>
          </div>
        } />

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="card border-l-4 border-yellow-500">
          <div className="text-xs text-gray-500">PF Due Date</div>
          <div className="text-sm font-semibold text-gray-700 mt-1">15th every month</div>
        </div>
        <div className="card border-l-4 border-blue-500">
          <div className="text-xs text-gray-500">ESI Due Date</div>
          <div className="text-sm font-semibold text-gray-700 mt-1">15th every month</div>
        </div>
        {(pfOverdue + esiOverdue) > 0 && (
          <div className="card border-l-4 border-red-500">
            <div className="text-xs text-gray-500">Pending Payments</div>
            <div className="text-sm font-semibold text-red-600 mt-1">{pfOverdue} PF + {esiOverdue} ESI</div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4">
          <SearchBar value={search} onChange={setSearch} placeholder="Search client..." />
          <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} className="input-field w-32">
            <option value=''>All Years</option>{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
          </select>
          <button onClick={() => setShowArchived(a => !a)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>{showArchived ? '📂 Archived' : '📁 Show Archived'}</button>
        </div>
                <BulkActionBar module="esipf" count={selected.size} staff={[]}
          statuses={['Pending','Paid','Paid Late']}
          onStatus={s => bulkUpdate({ pf_status: s, esi_status: s })}
          onDelete={bulkDelete}
          onWA={() => setWaTargets(bulkTargets())}
          onEmail={() => setEmailTargets(bulkTargets())}
          onClear={() => setSelected(new Set())} />
        <DataTable columns={columns} data={filtered} selected={selected} onToggle={toggleSel} onToggleAll={toggleAllSel} />
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit ESI/PF Record' : 'Add ESI/PF Record'} onClose={() => { setShowModal(false); setEditing(null); }}>
          <form onSubmit={handleSave} className="space-y-4">
            {!editing && <FormField label="Client" required>
              <ClientSearchDropdown
                value={form.client_id || ''}
                initialLabel=""
                service="ESI,PF"
                onChange={(id) => setForm(f => ({ ...f, client_id: id }))}
                required
              />
            </FormField>}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="FY"><select value={form.fy||'2025-26'} onChange={e => setForm(f => ({...f, fy: e.target.value}))} className="input-field">{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}</select></FormField>
              <FormField label="Month"><select value={form.month||''} onChange={e => setForm(f => ({...f, month: e.target.value}))} className="input-field"><option value="">Select</option>{MONTHS_FY.map(m => <option key={m}>{m}</option>)}</select></FormField>
              <FormField label="PF Amount (₹)"><input type="number" value={form.pf_amount||''} onChange={e => setForm(f => ({...f, pf_amount: e.target.value}))} className="input-field" /></FormField>
              <FormField label="PF Date Paid"><input type="date" value={form.pf_date_paid||''} onChange={e => setForm(f => ({...f, pf_date_paid: e.target.value}))} className="input-field" /></FormField>
              <FormField label="PF TRRN"><input value={form.pf_trrn||''} onChange={e => setForm(f => ({...f, pf_trrn: e.target.value}))} className="input-field" /></FormField>
              <FormField label="PF Status"><select value={form.pf_status||'Pending'} onChange={e => setForm(f => ({...f, pf_status: e.target.value}))} className="input-field"><option>Pending</option><option>Paid</option><option>Paid Late</option></select></FormField>
              <FormField label="ESI Amount (₹)"><input type="number" value={form.esi_amount||''} onChange={e => setForm(f => ({...f, esi_amount: e.target.value}))} className="input-field" /></FormField>
              <FormField label="ESI Date Paid"><input type="date" value={form.esi_date_paid||''} onChange={e => setForm(f => ({...f, esi_date_paid: e.target.value}))} className="input-field" /></FormField>
              <FormField label="ESI Challan"><input value={form.esi_challan||''} onChange={e => setForm(f => ({...f, esi_challan: e.target.value}))} className="input-field" /></FormField>
              <FormField label="ESI Status"><select value={form.esi_status||'Pending'} onChange={e => setForm(f => ({...f, esi_status: e.target.value}))} className="input-field"><option>Pending</option><option>Paid</option><option>Paid Late</option></select></FormField>
              <FormField label="Internal Due Date (PF/ESI)" className="col-span-2">
                <div className="flex gap-2">
                  <input type="date" value={form.internal_due_date||''} onChange={e => setForm(f => ({...f, internal_due_date: e.target.value}))} className="input-field flex-1" />
                  <button type="button" onClick={() => {
                    const [yr, mo] = (form.month || '').split(' ').reverse();
                    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                    const mIdx = MONTHS.indexOf(mo);
                    if (mIdx >= 0 && yr) {
                      const pfDue = `${yr}-${String(mIdx + 2 > 12 ? 1 : mIdx + 2).padStart(2,'0')}-15`;
                      setForm(f => ({ ...f, internal_due_date: calcInternalDue(pfDue, buffers.buffer_esipf) }));
                    }
                  }} className="text-xs border border-gray-200 rounded px-2 py-1.5 text-[#1e3a5f] hover:text-[#f0a500] whitespace-nowrap">Auto</button>
                </div>
                <p className="text-xs text-gray-400 mt-1">{buffers.buffer_esipf} days before PF/ESI due date (15th of next month)</p>
              </FormField>
            </div>
            <div className="flex gap-3"><button type="submit" className="btn-primary flex-1">Save</button><button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn-secondary flex-1">Cancel</button></div>
          </form>
        </Modal>
      )}
      {waTargets && <WhatsAppModal targets={waTargets} defaultMsg={`Dear Client,

This is a message from Krishna Uday & Associates regarding your compliance work.

Regards,
KU Associates`} onClose={() => setWaTargets(null)} />}
      {emailTargets && <BulkEmailModal targets={emailTargets} defaultSubject="Update from Krishna Uday & Associates" onClose={() => setEmailTargets(null)} />}
      {deleteTarget && <ConfirmModal message="Delete this ESI/PF record? This cannot be undone." onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />}
      {showImport && <ModuleImport module="esipf" onComplete={load} onClose={() => setShowImport(false)} />}
    </div>
  );
}
